/**
 * 네트워크가 불안정하거나 끊겼을 때 화면 맨 위에 뜨는 안내 띠.
 *
 * `pages/_app.tsx` 에서 앱 전체에 한 번 올려 둔다. 연결이 정상이면 아무것도 그리지 않는다.
 * 시험 도중 연결이 끊겨도 답안이 자동 저장된다는 사실을 알려 학생을 안심시키는 것이 목적이다.
 *
 * 주의: 이 띠는 화면에 고정으로 붙어 원래 내용을 가린다. 그래서 띠가 보이는 동안
 * `body` 에 `has-network-banner` 클래스를 붙여 본문을 아래로 밀어낸다.
 * 관련 CSS 는 `styles/global.css` 에 있으므로 클래스 이름을 바꾸려면 그쪽도 같이 고쳐야 한다.
 */
import React, { useEffect, useState } from "react";
import Image from "next/image";
import { useRecoilValue } from "recoil";
import { networkStatusState } from "@/recoil/atom";

// 실제 Recoil 상태를 사용하는 내부 컴포넌트
const NetworkStatusBannerContent: React.FC = () => {
  const status = useRecoilValue(networkStatusState);

  // 띠가 보이는 동안에만 body 에 표시를 남겨 본문을 아래로 밀어낸다.
  // 컴포넌트가 사라질 때도 반드시 지워야 본문 여백이 남아 도는 일이 없다.
  useEffect(() => {
    if (status !== "normal") {
      document.body.classList.add("has-network-banner");
    } else {
      document.body.classList.remove("has-network-banner");
    }
    return () => {
      document.body.classList.remove("has-network-banner");
    };
  }, [status]);

  if (status === "normal") return null;

  let bgColor = "";
  let textColor = "";
  let message = "";

  switch (status) {
    case "unstable":
      bgColor = "bg-warning-soft";
      textColor = "text-warning-ink";
      message = "네트워크가 불안정합니다. 답안은 자동 저장됩니다.";
      break;
    case "offline":
      bgColor = "bg-error-soft";
      textColor = "text-error-ink";
      message = "네트워크 연결이 끊겼습니다. 연결을 확인해주세요.";
      break;
    case "recovered":
      bgColor = "bg-success-soft";
      textColor = "text-success-ink";
      message = "네트워크가 복구됐습니다.";
      break;
    default:
      return null;
  }

  return (
    <div
      className={`fixed top-0 left-0 w-full h-[44px] z-[9999] px-4 py-3 flex justify-center items-center shadow-md transition-all duration-300 ${bgColor} ${textColor}`}
    >
      <div className="flex items-center gap-2">
        {status === "unstable" && <span className="text-lg">⚠️</span>}
        {status === "offline" && (
          <span
            className="w-5 h-5 flex items-center justify-center animate-spin"
            style={{ animationDuration: "1.4s" }}
            aria-hidden
          >
            <Image
              src="/images/haebom/haebom-symbol.png"
              alt=""
              width={18}
              height={14}
              priority
            />
          </span>
        )}
        {status === "recovered" && <span className="text-lg">✅</span>}
        <span className="text-14 font-medium">{message}</span>
      </div>
    </div>
  );
};

// 브라우저에 실제로 올라온 뒤에만 내용을 그리는 껍데기다.
// 빌드 시점에 미리 만들어 두는 HTML 에는 네트워크 상태가 없으므로,
// 미리 만든 결과와 브라우저가 그린 결과가 어긋나 경고가 나는 것을 피하려고 한 박자 늦춘다.
const NetworkStatusBanner: React.FC = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return <NetworkStatusBannerContent />;
};

export default NetworkStatusBanner;
